import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import '../common.css';

function IncomeIndex() {
  const { isAuthenticated, companyId, permissions, logout, token } = useAuth();
  const [data, setData] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const hasCreateTransactionPermission = permissions.includes('Create Transaction');
  const hasEditTransactionPermission = permissions.includes('Edit Transaction');
  const hasDeleteTransactionPermission = permissions.includes('Delete Transaction');

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
    fetchTableData();
  }, [isAuthenticated, companyId, token, logout]);

  const fetchTableData = async () => {
    setLoading(true);
    fetchData('IncomeApi', `/${companyId}`, token)
      .then((response) => {
        if (response.responseCode === '1111') {
          setData(response.response_data);
        }
      })
      .catch((error) => {
        console.error('Error fetching Income Details:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDelete = async () => {
    deleteData('IncomeApi', `${deleteItemId}`, token)
      .then(() => {
        fetchTableData();
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
      })
      .catch((error) => {
        console.error('Error deleting record:', error);
      });
  };

  const handleActivate = (itemId) => {
    activateData('IncomeApi', `${itemId}`, token)
      .then(() => {
        fetchTableData();
      })
      .catch((error) => {
        console.error('Error activating record:', error);
      });
  };

  const openConfirmDialog = (itemId) => {
    setDeleteItemId(itemId);
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setDeleteItemId(null);
    setConfirmDialogOpen(false);
  };

  const filteredData = data.filter((item) =>
    Object.values(item).some((value) =>
      String(value).toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  function formatDate(dateString) {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  return (
    <div className="page-container">
      {loading && (
        <div className="loading-overlay">
          <CircularProgress size={40} className="loading-spinner" />
          <span className="loading-text">Loading...</span>
        </div>
      )}

      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Income</h1>
        <div className="header-actions">
          <div className="search-box">
            <SearchIcon />
            <input
              type="text"
              className="search-input"
              placeholder="Search income..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          {hasCreateTransactionPermission && (
            <Link to="/IncomeCreate" className="create-btn">
              <AddIcon fontSize="small" />
              Add Income
            </Link>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Bill Date</th>
                <th>Bill No</th>
                <th>Account Head</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((item, index) => (
                  <tr key={item.Id}>
                    <td>{index + 1}</td>
                    <td className="cell-secondary">{formatDate(item.BillDate)}</td>
                    <td className="cell-primary">{item.BillNo}</td>
                    <td>{item.AccountHeadName}</td>
                    <td style={{ textAlign: 'right', fontWeight: '600', color: '#059669' }}>₹ {item.Amount.toFixed(2)}</td>
                    <td>
                      <div className="action-buttons">
                        {hasEditTransactionPermission && item.IsActive && (
                          <Link to={`/IncomeEdit/${item.Id}`}>
                            <button className="action-btn edit" title="Edit">
                              <EditIcon fontSize="small" />
                            </button>
                          </Link>
                        )}
                        {hasDeleteTransactionPermission && (
                          <button
                            className={`action-btn ${item.IsActive ? 'delete' : 'view'}`}
                            title={item.IsActive ? 'Delete' : 'Activate'}
                            onClick={() => item.IsActive ? openConfirmDialog(item.Id) : handleActivate(item.Id)}
                          >
                            {item.IsActive ? <DeleteIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6">
                    <div className="empty-state">
                      <p>No income records found</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Dialog */}
      <Dialog open={confirmDialogOpen} onClose={closeConfirmDialog}>
        <DialogTitle sx={{ fontWeight: 600, color: '#1e3a5f' }}>Delete Income?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#64748b' }}>
            Are you sure you want to delete this income record?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <button className="dialog-btn cancel" onClick={closeConfirmDialog}>Cancel</button>
          <button className="dialog-btn confirm" onClick={handleDelete}>Delete</button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default IncomeIndex;
